'use client';

/**
 * @fileoverview `DiscoverAccessCard` — rail-head access card for the signed-out
 * city Discover viewer (WEB-937). Occupies the taste-card slot anonymous
 * viewers can never fill: what an account adds, and that nothing below is
 * withheld. Claims **no** taste number.
 *
 * View/Connected split: `DiscoverAccessCardView` is pure props; the Connected
 * wrapper reads the SHARED discover subscription (cold-load single-fetch) and
 * existing signed-in signals (`useAuth().isAuthenticated`,
 * `authenticatedEnvelope`, `useDiscoverViewerId`) — no new auth probe.
 */
import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import { buttonClassName } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { useCityContext, useCityIsStaging } from '@/lib/city-context.client';
import { crossHostLogin, crossHostSignup } from '@/lib/cross-host-redirect';
import { defaultCityCode } from '@/lib/cross-host-urls';
import { useDiscoverViewerId } from '@/lib/discover/DiscoverViewerIdContext';
import {
  resolveDiscoverEnvelopeTotals,
  serializeDiscoverTotalsScope,
} from '@/lib/discover/envelopeTotals';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';

import {
  ACCESS_CARD_NEXT_PATH,
  ACCESS_CARD_PRIMARY_LABEL,
  ACCESS_CARD_SIGN_IN_LABEL,
  ACCESS_CARD_TITLE,
  anonymousAccessBodyWithCount,
  anonymousAccessBodyWithoutCount,
  resolveAnonymousAccessCountState,
  type AnonymousAccessCountState,
} from './anonymousAccessCopy';

/**
 * WEB-1026 — the DS md accent atom (`.a-btn--accent`, 40 tall, coarse-pointer
 * 44 `::before` hit floor, `--accent` → `--accent-hover` hover law), resting on
 * `--clt-skyline` (light #0E7B7B / dark #2FA3A3) instead of the Discover dark
 * scope's reconciled `--accent` (#4FC4C4, WEB-650). Without the rebind rest
 * equals hover there and the DS `.dv-anon__a:hover` lift is invisible.
 * Type, radius and padding stay the DS `.dv-anon__a` frame values
 * (`templates/discover.html`: display 13/600, `--r-sm`, 14px inline).
 */
const PRIMARY_LINK_CLASSES = cn(
  buttonClassName({ variant: 'accent' }),
  '[--accent:var(--clt-skyline)]',
  'rounded-sm px-3.5 font-display text-small font-semibold',
);

const SIGN_IN_LINK_CLASSES = cn(
  'text-accent-text underline underline-offset-2',
  'hover:decoration-2',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
  'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
);

export interface DiscoverAccessCardViewProps {
  readonly signupHref: string;
  readonly loginHref: string;
  readonly countState: AnonymousAccessCountState;
  readonly className?: string;
}

function AccessCardBody({
  countState,
}: {
  readonly countState: AnonymousAccessCountState;
}): React.ReactElement {
  if (countState.kind === 'non-default') {
    return (
      <p className="m-0 text-xs leading-normal text-fg-2">{anonymousAccessBodyWithoutCount()}</p>
    );
  }
  if (countState.kind === 'pending') {
    // WEB-1190 D5 — a pending total is not a number, so the body is the
    // without-count sentence rather than "All [skeleton] upcoming…".
    return (
      <p className="m-0 text-xs leading-normal text-fg-2">{anonymousAccessBodyWithoutCount()}</p>
    );
  }
  return (
    <p className="m-0 text-xs leading-normal text-fg-2">
      {anonymousAccessBodyWithCount(countState.total)}
    </p>
  );
}

/**
 * Pure presentational access card. Use this in Storybook and unit tests;
 * the Connected wrapper wires auth, totals, and cross-host hrefs.
 *
 * Styling matches v3 `.dv-anon`: dashed skyline border at 40%, wash at 6%,
 * same radius/padding as `.dv-taste` (`rounded-md` + 12px).
 */
export function DiscoverAccessCardView({
  signupHref,
  loginHref,
  countState,
  className,
}: DiscoverAccessCardViewProps): React.ReactElement {
  return (
    <section
      data-testid="discover-access-card"
      aria-label={ACCESS_CARD_TITLE}
      className={cn(
        'flex flex-col gap-2.5 rounded-md p-3',
        'border border-dashed border-[color-mix(in_srgb,var(--clt-skyline)_40%,transparent)]',
        'bg-[color-mix(in_srgb,var(--clt-skyline)_6%,transparent)]',
        className,
      )}
    >
      <b className="font-display text-sm font-semibold tracking-[-0.01em] text-foreground">
        {ACCESS_CARD_TITLE}
      </b>
      {/* Sheet overlay marks the results-bar live region inert; keep the
          count announcement on this card so a filter change still speaks. */}
      <div aria-live="polite" aria-atomic="true">
        <AccessCardBody countState={countState} />
      </div>
      <a
        href={signupHref}
        data-testid="discover-access-card-signup"
        className={PRIMARY_LINK_CLASSES}
      >
        {ACCESS_CARD_PRIMARY_LABEL}
      </a>
      <span
        data-testid="discover-access-card-footnote"
        className="font-mono text-micro uppercase tracking-[0.08em] text-fg-3"
      >
        Or{' '}
        <a
          href={loginHref}
          data-testid="discover-access-card-login"
          className={SIGN_IN_LINK_CLASSES}
        >
          {ACCESS_CARD_SIGN_IN_LABEL}
        </a>{' '}
        · nothing below is hidden
      </span>
    </section>
  );
}

export interface DiscoverAccessCardProps {
  readonly cityCode?: CityCode;
  /**
   * SSR envelope total for the default upcoming window until the shared
   * subscription lands (WEB-786). Applied only when {@link initialTotalsScope}
   * matches the live query args. Omitted when that scope is missing so a
   * late-mounted sheet cannot bind the SSR total to a filtered query.
   */
  readonly initialTotal?: number;
  /** `serializeDiscoverTotalsScope` of the SSR discover args, if any. */
  readonly initialTotalsScope?: string;
  readonly className?: string;
}

/**
 * Connected access card. Renders only for an anonymous viewer. A signed-in
 * viewer — with or without a taste profile — gets nothing (the taste card
 * owns the slot when a real `tasteMatchPct` exists).
 */
export function DiscoverAccessCard({
  cityCode,
  initialTotal,
  initialTotalsScope,
  className,
}: DiscoverAccessCardProps): React.ReactElement | null {
  const city = useCityContext();
  const resolvedCityCode = cityCode ?? city?.code ?? defaultCityCode();
  const isStaging = useCityIsStaging();
  const { isAuthenticated } = useAuth();
  const viewerId = useDiscoverViewerId();

  const { params } = useDiscoverParams();
  const queryArgs = useDiscoverEventArgs(params, resolvedCityCode);
  const { currentData, data } = useDiscoverEventsQuery(queryArgs);

  const scopeKey = React.useMemo(() => serializeDiscoverTotalsScope(queryArgs), [queryArgs]);
  const fallbackApplies = initialTotalsScope !== undefined && scopeKey === initialTotalsScope;
  const totals = resolveDiscoverEnvelopeTotals(currentData, {
    inRangeTotal: fallbackApplies ? initialTotal : undefined,
  });
  const countState = resolveAnonymousAccessCountState(
    params.date,
    totals.resolved ? totals.inRangeTotal : undefined,
  );
  const signupHref = crossHostSignup(ACCESS_CARD_NEXT_PATH, { isStaging });
  const loginHref = crossHostLogin(ACCESS_CARD_NEXT_PATH, { isStaging });

  const envelope = currentData?.authenticatedEnvelope ?? data?.authenticatedEnvelope;
  if (viewerId || isAuthenticated || envelope) {
    return null;
  }

  return (
    <DiscoverAccessCardView
      signupHref={signupHref}
      loginHref={loginHref}
      countState={countState}
      className={className}
    />
  );
}
